import React, { useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { integrationApiService } from '../services/integrationApi';
import type {
  IntegrationConfig,
  RebateDiscountSettings,
  ShopifyRebateDiscountSyncState,
} from '../services/integrationApi';
import { rebateDiscountOn } from './integrationSetup/rebateDiscountSync';
import RebateDiscountChoice from './RebateDiscountChoice';
import type { RebateDiscountKind } from './RebateDiscountChoice';

export interface ShopifyRebateDiscountsSectionProps {
  integration: IntegrationConfig;
  /** The store's own switches (`settings.rebateDiscounts`), edited here and saved by the caller. */
  rebateDiscounts?: RebateDiscountSettings;
  onRebateDiscountsChange?: (next: RebateDiscountSettings) => void;
}

const formatWhen = (value: unknown): string => {
  if (!value) return '';
  const record = value as Record<string, any>;
  const seconds = typeof record.seconds === 'number' ? record.seconds : typeof record._seconds === 'number' ? record._seconds : null;
  const date = seconds != null ? new Date(seconds * 1000) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

interface DiscountBlockProps {
  integration: IntegrationConfig;
  /** i18n prefix holding title, subtitle, never, last, cap, errors, failed */
  prefix: string;
  last: ShopifyRebateDiscountSyncState | undefined;
  runSync: () => Promise<ShopifyRebateDiscountSyncState>;
  /** The store's switch for this kind, shown above the last result when the walkthrough flag is on. */
  choice?: { kind: RebateDiscountKind; checked: boolean; onChange: (checked: boolean) => void };
}

/** One sync card: last result, Sync now, cap warning and the reconnect state. */
const DiscountBlock: React.FC<DiscountBlockProps> = ({ integration, prefix, last, runSync, choice }) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const shared = 'integrations.rebateDiscounts';

  const sync = useMutation({
    mutationFn: runSync,
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err: Error) => setError(err.message || t(`${prefix}.failed`)),
  });

  const lock = integration.scopeStatus?.settingLocks?.writeDiscounts;
  // A known grant is trusted over a prior failed run's stored scopeStatus
  // (rebateDiscountSync / bundleDiscountSync), which stays reconnect_required until Sync now succeeds (trusting it would leave
  // the button disabled after the store already reconnected). An unknown grant
  // (`granted` null: the install predates scope recording) is unlocked in the lock
  // map but the server treats it as not granted, so there the last run decides.
  const grantUnknown = !Array.isArray(integration.scopeStatus?.granted);
  const needsReconnect = lock?.locked === true || (grantUnknown && last?.scopeStatus === 'reconnect_required');
  const customApp = integration.credentials?.installMethod !== 'public_oauth';

  const reconnect = async () => {
    const shop = String(integration.credentials?.shopId || integration.credentials?.shopDomain || '');
    if (!shop || customApp) return;
    setReconnecting(true);
    try {
      const data = await integrationApiService.startShopifyInstall(shop, { scopeGroups: ['discounts'] });
      window.location.assign(data.installUrl);
    } catch (err) {
      setReconnecting(false);
      setError(err instanceof Error ? err.message : t(`${prefix}.failed`));
    }
  };

  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOffer fontSize="small" color="action" />
          <Typography variant="subtitle2">{t(`${prefix}.title`)}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
          {t(`${prefix}.subtitle`)}
        </Typography>
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {choice && (
          <RebateDiscountChoice
            kind={choice.kind}
            checked={choice.checked}
            onChange={choice.onChange}
            disabled={needsReconnect}
          />
        )}
        {needsReconnect && (
          <Alert
            severity="warning"
            action={
              !customApp ? (
                <Button size="small" color="inherit" onClick={reconnect} disabled={reconnecting}>
                  {t(`${shared}.reconnectButton`)}
                </Button>
              ) : undefined
            }
          >
            {customApp ? t(`${shared}.customApp`) : t(`${shared}.reconnect`)}
          </Alert>
        )}
        {!needsReconnect && (last?.capSkipped || 0) > 0 && (
          <Alert severity="warning">{t(`${prefix}.cap`, { count: last!.capSkipped })}</Alert>
        )}
        {!needsReconnect && (last?.errorCount || 0) > 0 && (
          <Alert severity="error">{t(`${prefix}.errors`, { count: last!.errorCount })}</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2" color="text.secondary">
          {last
            ? t(`${prefix}.last`, {
                when: formatWhen(last.at),
                created: last.created,
                updated: last.updated,
                deleted: last.deleted,
                skipped: last.skipped,
              })
            : t(`${prefix}.never`)}
        </Typography>
        <Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => sync.mutate()}
            disabled={sync.isPending || needsReconnect}
          >
            {sync.isPending ? t(`${shared}.syncing`) : t(`${shared}.syncNow`)}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * Shopify automatic discounts Motzu keeps for a store: one for each live
 * instant rebate under the "separate discount line" treatment (flag
 * `shopify_rebate_discounts`) and, in a second block, one buy-X-get-Y discount
 * per active bundle rebate (flag `bundle_shopify_discounts`).
 */
const ShopifyRebateDiscountsSection: React.FC<ShopifyRebateDiscountsSectionProps> = ({
  integration,
  rebateDiscounts,
  onRebateDiscountsChange,
}) => {
  const { enabled: rebatesOn } = useOrgFeatureFlag('shopify_rebate_discounts');
  const { enabled: bundlesOn } = useOrgFeatureFlag('bundle_shopify_discounts');
  // The store-level switches belong to the setup walkthrough flag (base plan).
  const { enabled: choiceOn } = useOrgFeatureFlag('integration_setup_walkthrough');
  const choiceFor = (kind: RebateDiscountKind) =>
    choiceOn && onRebateDiscountsChange
      ? {
          kind,
          checked: rebateDiscountOn(rebateDiscounts, kind),
          onChange: (checked: boolean) => onRebateDiscountsChange({ ...rebateDiscounts, [kind]: checked }),
        }
      : undefined;
  if (!rebatesOn && !bundlesOn) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {rebatesOn && (
        <DiscountBlock
          integration={integration}
          prefix="integrations.rebateDiscounts"
          last={integration.rebateDiscountSync}
          runSync={() => integrationApiService.syncShopifyRebateDiscounts(integration.id)}
          choice={choiceFor('instant')}
        />
      )}
      {bundlesOn && (
        <DiscountBlock
          integration={integration}
          prefix="integrations.rebateDiscounts.bundle"
          last={integration.bundleDiscountSync}
          runSync={() => integrationApiService.syncShopifyBundleDiscounts(integration.id)}
          choice={choiceFor('bundle')}
        />
      )}
    </Box>
  );
};

export default ShopifyRebateDiscountsSection;
